/**
 * Glass Refined — shell theme tokens.
 *
 * Central Tailwind class-string constants for the host shell (ShellLayout,
 * __root.tsx) and the Sidenav component. Every custom hex / rgba value used
 * for the shell aesthetic lives here, so re-skinning the shell means editing
 * exactly this file.
 *
 * Why full Tailwind class strings and not raw hex? Tailwind's JIT scanner
 * picks up class-literal strings from source files. If the literal
 * `"bg-[#0d1b2a]"` appears anywhere in the scanned source, Tailwind emits
 * the corresponding CSS. A runtime `bg-[${color}]` interpolation would be
 * invisible to the scanner and would not generate CSS — so these constants
 * must be whole class strings, not raw color values.
 *
 * The `packages/ui/tailwind.config.js` content glob must include this file's
 * parent directory, otherwise the classes defined here will be purged.
 */

// ─── Shell canvas (ShellLayout / __root.tsx) ───────────────────────────────

/**
 * Dark-mode background for the flex-col wrapper that holds Header + main.
 * Only kicks in when `[data-theme="dark"]` is on the body. Light mode falls
 * back to whatever bg the consumer sets (typically `bg-white`).
 */
export const SHELL_CANVAS_DARK = "dark:bg-[#0d1b2a]";

// ─── Sidenav canvas ────────────────────────────────────────────────────────

/**
 * Radial navy gradient — self-contained background for the sidenav panel so
 * it reads as a dark island regardless of what surrounds it.
 */
export const SIDENAV_CANVAS =
  "bg-[radial-gradient(ellipse_at_top_left,#0d1b2a_0%,#000814_70%)]";

/**
 * Ambient glow orb overlay — two stacked radial gradients applied to a
 * sibling absolute div behind the sidenav content. Provides the subtle
 * top-left / bottom-right glow atmosphere.
 */
export const SIDENAV_ORB_OVERLAY =
  "bg-[radial-gradient(circle_at_15%_20%,rgba(94,179,255,0.08),transparent_45%),radial-gradient(circle_at_85%_85%,rgba(94,179,255,0.06),transparent_45%)]";

/** Soft drop shadow that lifts the sidenav off lighter surrounding surfaces. */
export const SIDENAV_SHADOW = "shadow-[4px_0_32px_rgba(15,23,42,0.08)]";

// ─── Accent tokens (muted sky blue) ────────────────────────────────────────

/** 2px vertical accent bar on active nav items. Full-color. */
export const ACCENT_BAR = "bg-[#5eb3ff]";

/**
 * `before:` variant of the accent bar — used to style the pseudo-element
 * indicator on active nav items. Needs its own entry because Tailwind JIT
 * requires the full prefixed class as a literal string.
 */
export const ACCENT_BAR_BEFORE = "before:bg-[#5eb3ff]";

/** Soft accent background tint — expanded active state. 8% opacity. */
export const ACCENT_TINT_SOFT = "bg-[rgba(94,179,255,0.08)]";

/**
 * Hover variant of the soft accent tint. Needs its own entry because
 * Tailwind JIT requires the `hover:` prefix as part of a literal string —
 * it can't be derived from ACCENT_TINT_SOFT at runtime.
 */
export const ACCENT_TINT_SOFT_HOVER = "hover:bg-[rgba(94,179,255,0.08)]";

/**
 * Stronger accent tint — used for the collapsed active state, where the 2px
 * left bar sits outside the sidenav and isn't visible. The stronger tint
 * compensates so the active item still reads clearly. 14% opacity.
 */
export const ACCENT_TINT_STRONG = "bg-[rgba(94,179,255,0.14)]";
